import {
  ENTITY,
  PhysicalCondition,
  TemperatureCondition,
  UserActions,
} from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import {
  isInvoicePricingNotFoundError,
  notifyInvoicePricingNotFound,
} from '@/pages/authenPages/Order/invoicePricingError';
import {
  isInvoiceError,
  notifyInvoiceError,
} from '@/pages/authenPages/Order/invoiceError';
import {
  isProductsOutOfStockError,
  notifyProductsOutOfStock,
} from '@/pages/authenPages/Order/productsOutOfStockError';
import {
  CreateOrderEntity,
  OrderEntity,
  OrderParams,
  OrderStatusEntity,
  UpdateOrderEntity,
} from '@/pages/privatePages/order/types';
import { QueryKeyTypes, newQueryKeys } from '@/queries/queryKeys';
import { passPageParams } from '@/queries/utils';
import {
  OrderCountParams,
  orderService,
  RegenerateInvoiceDocumentsResponse,
} from '@/services/api-services';
import { CustomResponse } from '@/services/api-services/type';
import { CustomError, errorNotification } from '@/utils';
import {
  InfiniteData,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

type DefaultQuery = Partial<
  UseQueryOptions<
    CustomResponse<OrderEntity[]>,
    Error,
    CustomResponse<OrderEntity[]>,
    QueryKeyTypes<OrderParams>
  >
>;

type GetOrderHistoryParams = Partial<
  UseQueryOptions<
    OrderStatusEntity[],
    Error,
    OrderStatusEntity[],
    QueryKeyTypes<OrderParams>
  >
>;

type DetailQuery = Partial<
  UseQueryOptions<OrderEntity, Error, OrderEntity, QueryKeyTypes>
>;

type InfiniteQuery = Partial<
  UseInfiniteQueryOptions<
    CustomResponse<OrderEntity[]>,
    Error,
    InfiniteData<CustomResponse<OrderEntity[]>>,
    CustomResponse<OrderEntity[]>,
    QueryKeyTypes<OrderParams>,
    number
  >
>;

type MutationCreate = UseMutationOptions<
  OrderEntity[],
  Error & { response?: any },
  CreateOrderEntity
>;

type MutationUpdate = UseMutationOptions<
  OrderEntity,
  Error & { response?: any },
  UpdateOrderEntity
>;

type MutationDelete = UseMutationOptions<unknown, Error, string>;

export const useGetOrder = (
  props: { params?: OrderParams; options?: DefaultQuery } = {},
) => {
  const { params = {}, options = {} } = props;
  const { canGetOrders } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ORDER, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.order.list(passPageParams(params)),
    queryFn: orderService.getCollapsed,
    ...options,
    enabled: (options.enabled ?? true) && canGetOrders,
  });

  return query;
};

export const useGetOrderStatusQuery = (
  props: { params?: OrderParams; options?: GetOrderHistoryParams } = {},
) => {
  const { params = {}, options = {} } = props;
  const { canGetOrderStatus } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ORDER_STATUS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.orderStatus.list(params),
    queryFn: orderService.getOrderHistory,
    ...options,
    enabled: (options.enabled ?? true) && canGetOrderStatus,
  });

  return query;
};

export const useGetInfiniteOrder = (
  props: { params?: OrderParams; options?: InfiniteQuery } = {},
) => {
  const { params = {}, options = {} } = props;
  const { canGetOrders } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ORDER, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey: newQueryKeys.order.list(passPageParams(params)),
    queryFn: orderService.getInfiniteCollapsed,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetOrders,
  });

  return query;
};

export const useGetOrderById = (props: {
  id: string;
  options?: DetailQuery;
}) => {
  const { id, options = {} } = props ?? {};
  const { canGetOrders } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ORDER, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.order.detail(id),
    queryFn: orderService.getByIdCollapsed,
    ...options,
    enabled: (options.enabled ?? true) && canGetOrders,
  });

  return query;
};

export const useCreateOrder = (options: MutationCreate = {}) => {
  const mutation = useMutation({
    mutationFn: orderService.createOrder,
    mutationKey: newQueryKeys.order.invalidateListsKey(),
    meta: { showError: false },
    // onError: (error, vars, context) => {
    //   if (error?.response?.data?.errorCode === ErrorCode.PRODUCTS_OUT_OF_STOCK) {
    //     return errorNotification({
    //       error: { errorCode: ErrorCode.PRODUCTS_OUT_OF_STOCK },
    //       contentObj: { productNames: error?.response?.data?.productNames },
    //     });
    //   } else {
    //     errorNotification({ error: error as unknown as CustomError });
    //   }
    // },
    ...options,
  });

  return mutation;
};

export const useUpdateOrder = (options: MutationUpdate = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: orderService.updateOrder,
    mutationKey: newQueryKeys.order.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      const { id = '' } = variables;
      onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.order.detail(id),
      });
    },
    meta: { showError: false },
    onError: (error, vars, context) => {
      // Whole update is rolled back server-side; form state is left as-is.
      if (isInvoicePricingNotFoundError(error)) {
        return notifyInvoicePricingNotFound(error);
      }
      // Lists each short product with how many it needs vs. has left.
      if (isProductsOutOfStockError(error)) {
        return notifyProductsOutOfStock(error);
      }
      // Eligible but Xero couldn't create the invoice - lists Xero's
      // validation errors.
      if (isInvoiceError(error)) {
        return notifyInvoiceError(error);
      }
      errorNotification({ error: error as unknown as CustomError });
    },
    ...rest,
  });

  return mutation;
};

export const useRegenerateInvoiceDocuments = (
  options: UseMutationOptions<
    RegenerateInvoiceDocumentsResponse,
    Error & { response?: any },
    string
  > = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: orderService.regenerateInvoiceDocuments,
    mutationKey: newQueryKeys.order.invalidateListsKey(),
    onSuccess: (data, id, context) => {
      onSuccess?.(data, id, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.order.detail(id),
      });
    },
    meta: { showError: false },
    onError: error => {
      if (isInvoiceError(error)) {
        return notifyInvoiceError(error);
      }
      errorNotification({ error: error as unknown as CustomError });
    },
    ...rest,
  });

  return mutation;
};

export const useDeleteOrder = (options: MutationDelete = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: orderService.delete,
    mutationKey: newQueryKeys.order.invalidateListsKey(),
    onSuccess: (data, id, context) => {
      onSuccess?.(data, id, context);
      queryClient.removeQueries({
        queryKey: newQueryKeys.order.detail(id),
        exact: true,
      });
    },
    ...rest,
  });

  return mutation;
};

type UpdateMultipleOrdersStatusPayload = {
  ids?: string[];
  orderStatus: string;
  orderIds?: string[];
  currentStatus?: string;
  orderConditionOnReceived?: {
    temperature: TemperatureCondition;
    physical: PhysicalCondition;
  };
};

export const useUpdateMultipleOrdersStatus = (
  options: UseMutationOptions<
    unknown,
    Error & { response?: any },
    UpdateMultipleOrdersStatusPayload
  > = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;

  const mutation = useMutation({
    mutationFn: orderService.updateMultipleOrdersStatus,
    mutationKey: newQueryKeys.order.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      const { ids = [] } = variables;
      onSuccess?.(data, variables, context);
      ids.forEach(id => {
        queryClient.invalidateQueries({
          queryKey: newQueryKeys.order.detail(id),
        });
      });
    },
    ...rest,
  });

  return mutation;
};

type CountQuery = Partial<
  UseQueryOptions<number, Error, number, QueryKeyTypes<OrderCountParams>>
>;

export const useGetCountToProcessOrder = (props: {
  params: OrderCountParams;
  options?: CountQuery;
}) => {
  const { params, options = {} } = props;
  const { canGetOrders } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ORDER, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useQuery({
    queryKey: newQueryKeys.order.detail('count-to-process', {
      params,
    }) as QueryKeyTypes<OrderCountParams>,
    queryFn: () => orderService.count(params),
    ...options,
    enabled: (options.enabled ?? true) && canGetOrders,
  });

  return query;
};
